/**
 * Changed files → the Apiboo items they are (git sync Stage 1, card 7 set 7b).
 *
 * The Git page shows changes "in Apiboo terms, not YAML" (owner, 28.09.2026):
 * a request with its changed fields, an environment with its changed
 * variables (secret values always ••••), a collection's settings, its folders
 * one by one (they live inside collection.yaml). Pure: the caller hands in
 * each file's text before (last sync) and after (now).
 *
 * @typedef {{ path: string, kind: 'added'|'modified'|'deleted', before: string|null, after: string|null }} ChangedFile
 * @typedef {{ label: string, change: 'added'|'removed'|'changed', before?: string, after?: string }} FieldChange
 * @typedef {{ id: string, group: string, kind: 'added'|'modified'|'deleted', name: string, path: string, fields: FieldChange[] }} ChangeItem
 */
import { parseYaml } from '../../utils/yamlWorkspace/deterministicYaml.js';
import { pairRenames } from './renamePairs.js';
import { SCOPE_GROUPS, SCOPE_NOUNS, scopeGroupOfPath, scopeItemName, scopeFieldChanges } from './scopeRepoLayout.js';

export const GROUPS = ['Requests', 'Folders', 'Collections', 'Environments', 'Variables', ...SCOPE_GROUPS, 'Other'];
export const MASK = '••••';

const LABELS = {
  name: 'Name',
  method: 'Method',
  url: 'URL',
  description: 'Description',
  headers: 'Headers',
  params: 'Query params',
  cookies: 'Cookies',
  body_type: 'Body type',
  body: 'Body',
  auth: 'Auth',
  variables: 'Variables',
  settings: 'Settings',
  sort_order: 'Position',
  mock: 'Mock',
};
// Bookkeeping a person did not change: the id never changes; these follow cloud sync.
const QUIET = new Set(['id', 'requests', 'local_uid', 'cloud_collection_id', 'link_status', 'last_synced_at', 'version']);

// Fields in the order a person reads a request; anything else after, A–Z.
const ORDER = ['name', 'method', 'url', 'params', 'headers', 'cookies', 'auth', 'body_type', 'body', 'description', 'variables', 'settings'];
const byReadingOrder = (a, b) => {
  const ia = ORDER.indexOf(a);
  const ib = ORDER.indexOf(b);
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return a.localeCompare(b);
};

const label = (key) => LABELS[key] || key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function safeParse(text) {
  if (text == null) return null;
  try {
    const v = parseYaml(text);
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

function short(value) {
  if (value === undefined || value === null || value === '') return '—';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return s.length > 140 ? `${s.slice(0, 137)}…` : s;
}

const isRowList = (v) => Array.isArray(v) && v.every((r) => r && typeof r === 'object' && 'key' in r);
const rowValue = (r) => (r?.secret ? MASK : short(r?.value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** key/value rows (headers, params, variables) compared by key. */
function diffRows(rowLabel, before = [], after = []) {
  const out = [];
  const old = new Map(before.map((r) => [r.key, r]));
  const now = new Map(after.map((r) => [r.key, r]));
  for (const [k, r] of now) {
    if (!old.has(k)) out.push({ label: `${rowLabel} ${k}`, change: 'added', after: rowValue(r) });
    else if (!same(old.get(k), r)) {
      const o = old.get(k);
      const what = o.enabled !== r.enabled && same({ ...o, enabled: r.enabled }, r)
        ? { before: o.enabled === false ? 'off' : 'on', after: r.enabled === false ? 'off' : 'on' }
        : { before: rowValue(o), after: rowValue(r) };
      out.push({ label: `${rowLabel} ${k}`, change: 'changed', ...what });
    }
  }
  for (const [k, r] of old) if (!now.has(k)) out.push({ label: `${rowLabel} ${k}`, change: 'removed', before: rowValue(r) });
  return out;
}

const ROW_LABEL = { headers: 'Header', params: 'Param', cookies: 'Cookie', variables: 'Variable' };

/** Field-by-field changes between two parsed objects (top level, rows by key). */
export function diffFields(before = {}, after = {}, { skip = [] } = {}) {
  const out = [];
  const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])].sort(byReadingOrder);
  for (const key of keys) {
    if (QUIET.has(key) || skip.includes(key)) continue;
    const a = before?.[key];
    const b = after?.[key];
    if (same(a, b)) continue;
    if ((isRowList(a) || a === undefined) && (isRowList(b) || b === undefined) && (a || b)) {
      out.push(...diffRows(ROW_LABEL[key] || label(key), a || [], b || []));
    } else if (a === undefined) out.push({ label: label(key), change: 'added', after: short(b) });
    else if (b === undefined) out.push({ label: label(key), change: 'removed', before: short(a) });
    else out.push({ label: label(key), change: 'changed', before: short(a), after: short(b) });
  }
  return out;
}

function flattenFolders(folders = [], acc = new Map()) {
  for (const f of folders || []) {
    if (!f || typeof f !== 'object') continue;
    if (f.id) acc.set(f.id, f);
    if (Array.isArray(f.folders)) flattenFolders(f.folders, acc);
    if (Array.isArray(f.children)) flattenFolders(f.children, acc);
  }
  return acc;
}

/** collection.yaml → the collection itself plus one item per changed folder. */
function collectionItems(file, before, after) {
  const items = [];
  const collName = after?.name || before?.name || 'Collection';
  const fields = diffFields(before || {}, after || {}, { skip: ['folders'] });
  if (file.kind !== 'modified' || fields.length) {
    items.push({ group: 'Collections', kind: file.kind, name: collName, path: file.path, fields: file.kind === 'modified' ? fields : [] });
  }
  const old = flattenFolders(before?.folders);
  const now = flattenFolders(after?.folders);
  const strip = (f) => {
    const { folders: _a, children: _b, ...rest } = f;
    return rest;
  };
  for (const [id, f] of now) {
    const name = `${collName} › ${f.name || 'Folder'}`;
    if (!old.has(id)) items.push({ group: 'Folders', kind: 'added', name, path: file.path, fields: [] });
    else {
      const ch = diffFields(strip(old.get(id)), strip(f));
      if (ch.length) items.push({ group: 'Folders', kind: 'modified', name, path: file.path, fields: ch });
    }
  }
  for (const [id, f] of old) {
    if (!now.has(id)) items.push({ group: 'Folders', kind: 'deleted', name: `${collName} › ${f.name || 'Folder'}`, path: file.path, fields: [] });
  }
  return items;
}

/** One changed file of a scope repository, in Apiboo's words. */
function scopeItemFor(file) {
  const p = file.path;
  const group = scopeGroupOfPath(p);
  if (!group) return [{ group: 'Other', kind: file.kind, name: p, path: p, fields: [] }];
  const name = scopeItemName(p, file.after ?? file.before);
  const fields = file.kind === 'modified' ? scopeFieldChanges(p, file.before, file.after) : [];
  return [{ group, kind: file.kind, name, path: p, fields }];
}

function itemFor(file) {
  const before = safeParse(file.before);
  const after = safeParse(file.after);
  const name = after?.name || before?.name;
  const p = file.path;
  const fields = file.kind === 'modified' ? diffFields(before || {}, after || {}) : [];
  const one = (group, n) => [{ group, kind: file.kind, name: n, path: p, fields }];

  if (/^collections\/[^/]+\/collection\.yaml$/.test(p)) return collectionItems(file, before, after);
  if (/^collections\/[^/]+\/requests\/[^/]+\.yaml$/.test(p)) return one('Requests', name || p.split('/').pop().replace(/\.yaml$/, ''));
  if (/^environments\/[^/]+\.yaml$/.test(p)) return one('Environments', name || 'Environment');
  if (p === 'variables/globals.yaml') return one('Variables', 'Global variables');
  if (p === 'variables/workspace.yaml') return one('Variables', 'Workspace variables');
  if (p === 'apiboo.yaml') return one('Other', 'Workspace settings');
  if (p.startsWith('schemas/')) return one('Other', 'Schemas');
  if (p === '.gitignore' || p === '.gitattributes') return [{ group: 'Other', kind: file.kind, name: 'Git setup files', path: p, fields: [] }];
  const legacy = nestedKind(p);
  if (legacy === 'Requests') return one('Requests', name || p.split('/').pop().replace(/\.yaml$/, ''));
  if (legacy === 'Folders') return one('Folders', name || p.split('/').slice(-2, -1)[0]);
  if (legacy === 'Collections') return one('Collections', name || p.split('/')[0]);
  return [{ group: 'Other', kind: file.kind, name: p, path: p, fields: [] }];
}

/** The group a file belongs to, from its path alone (history lists, before any file is read). */
/**
 * `layout`: 'workspace' (default) or 'scope' — a scope repository (boards /
 * documents / projects, git sync Stage 2) has its own layout; in a workspace
 * repository a top folder named "documents" is a collection, not documents.
 */
export function groupOfPath(p = '', layout = 'workspace') {
  if (layout === 'scope') return scopeGroupOfPath(p) || 'Other';
  if (/^collections\/[^/]+\/requests\/[^/]+\.yaml$/.test(p)) return 'Requests';
  if (/^collections\/[^/]+\/collection\.yaml$/.test(p)) return 'Collections';
  if (/^environments\/[^/]+\.yaml$/.test(p)) return 'Environments';
  if (/^variables\//.test(p)) return 'Variables';
  const legacy = nestedKind(p);
  if (legacy) return legacy;
  return 'Other';
}

// The nested mirror layout (workspaceMirror.js): <collection>/collection.yaml,
// <collection>/<folder…>/folder.yaml, every other .yaml in a collection folder is
// a request. Not the reserved top-level folders, not dotfiles.
const RESERVED_TOP = new Set(['variables', 'environments', 'schemas', 'collections']);
function nestedKind(p) {
  const parts = p.split('/');
  if (parts.length < 2 || RESERVED_TOP.has(parts[0]) || parts[0].startsWith('.') || !p.endsWith('.yaml')) return null;
  if (parts.length === 2 && parts[1] === 'collection.yaml') return 'Collections';
  if (parts[parts.length - 1] === 'folder.yaml') return 'Folders';
  return 'Requests';
}

/** @param {ChangedFile[]} files  @returns {ChangeItem[]} grouped in GROUPS order, then by name */
export function describeChanges(files = [], layout = 'workspace') {
  const raw = files.flatMap((f) => (layout === 'scope' ? scopeItemFor(f) : itemFor(f)));
  // A rename is one change (renamePairs.js) — workspace layouts, where every item has an id.
  const paired = layout === 'scope' ? raw : pairRenames(raw, new Map(files.map((f) => [f.path, f])), diffFields);
  const items = paired.map((it, i) => ({ id: `${it.path}#${i}`, ...it }));
  return items.sort((a, b) => GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group) || a.name.localeCompare(b.name));
}

/** "2 requests, 1 environment" — also the readable commit message of set 7d. */
export function summarizeChanges(items = []) {
  const counts = new Map();
  for (const it of items) counts.set(it.group, (counts.get(it.group) || 0) + 1);
  const noun = { Requests: 'request', Folders: 'folder', Collections: 'collection', Environments: 'environment', Variables: 'variable set', ...SCOPE_NOUNS, Other: 'other change' };
  return GROUPS.filter((g) => counts.has(g))
    .map((g) => `${counts.get(g)} ${noun[g]}${counts.get(g) === 1 ? '' : 's'}`)
    .join(', ');
}

/**
 * Raw line diff of one file (for "Show file diff"): [{ op: ' '|'+'|'-', text }].
 * Plain LCS — workspace files are small; huge ones fall back to before/after.
 */
export function lineDiff(before = '', after = '') {
  const a = (before || '').split('\n');
  const b = (after || '').split('\n');
  if (a.length * b.length > 4_000_000) {
    return [...a.map((text) => ({ op: '-', text })), ...b.map((text) => ({ op: '+', text }))];
  }
  const dp = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ op: ' ', text: a[i] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: '-', text: a[i] });
      i += 1;
    } else {
      out.push({ op: '+', text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) out.push({ op: '-', text: a[i++] });
  while (j < b.length) out.push({ op: '+', text: b[j++] });
  return out;
}
